/**
 * 2026-09-22 one-owner audit (defect #4) — a cost goes onto a client-invoice
 * line in the INVOICE's currency. Same currency: as is. Different currency:
 * a unit change converts with the rate pinned on the source catalogue row
 * (`fxRateAtPurchase` — TT$ per US$, the same convention `InventoryItem`
 * .costing.fxRateAtPurchase / `ClientInvoiceItem`.fxRateAtCost already use;
 * see invariant 5, rates are pinned at write time and never recomputed), an
 * imported cost with the D119.9 ladder's rate (D157.5). No
 * rate available: refuse rather than guess — the caller leaves the line's
 * cost untouched and surfaces why, the same way it already does when the item
 * isn't in the inventory catalogue at all.
 *
 * `ClientInvoicePage.tsx`'s UOM-change cascade used to pick whichever of the
 * catalogue row's US$/TT$ cost tiers was non-zero (`newUsdCost > 0 ?
 * newUsdCost : newTtCost`) with no regard for which currency the invoice was
 * actually in — a TT$-only cost tier landed on a US$ invoice unconverted,
 * about 6.5x too high.
 *
 * Both resolvers convert with NO rounding (D119.10: a line stores its exact cost in the invoice
 * currency and the selling price is rounded up once from it) through the owners
 * `costToUsdExact` (`utils/fxDisplay.ts`) / `usdToTtdExact` (`utils/pricing`, D119.17; BUSINESS_RULES#usd-cost-and-ttd-display).
 * They take `ttdToUsd` (US$ per TT$1) while `fxRateAtPurchase` is TT$ per US$1, inverted once here.
 */

import { costToUsdExact } from '../../utils/fxDisplay';
import { exactCostText, sellingPriceAt, usdToTtdExact } from '../../utils/pricing';
import { roundQuantity } from '../../utils/packConversion';

export type SupportedInvoiceCurrency = 'USD' | 'TTD';

const NO_CURRENCY_REASON = 'the invoice has no currency yet - pick one first';

export interface UomCascadeCostInputs {
  /** The client invoice's own currency. Only USD/TTD have a conversion path. */
  invoiceCurrency: string | null | undefined;
  /** The catalogue row's TT$ figure for the target tier; 0 when it states none. */
  ttCost: number;
  /** The catalogue row's US$ figure for the target tier; 0 when it states none. */
  usdCost: number;
  /** TT$ per US$ pinned on the catalogue row at its last purchase, or falsy when absent. */
  fxRateAtPurchase: number | null | undefined;
}

/**
 * D119.10 (D85): `cost` is the line's cost rate in the INVOICE's currency, EXACT - converted with no
 * rounding either way, because the markup goes on the exact cost and is rounded up once
 * (`sellingPriceAt`). A rounded or ceiled cost here sold TT$2.00 at 6.0 with 30% at 0.43, not 0.44,
 * and US$0.95 at 6.78 with 30% at 8.39, not 8.38. The line stores this figure as it is.
 */
export type UomCascadeCostResult =
  | { cost: number; refusalReason?: undefined }
  | { cost: null; refusalReason: string };

export function resolveUomCascadeCost({
  invoiceCurrency,
  ttCost,
  usdCost,
  fxRateAtPurchase,
}: UomCascadeCostInputs): UomCascadeCostResult {
  const currency = String(invoiceCurrency || '').trim().toUpperCase();
  // D96.14: no currency chosen yet - never assume USD; the caller leaves the cost alone.
  if (!currency) return { cost: null, refusalReason: NO_CURRENCY_REASON };
  // fxRateAtPurchase is TT$ per US$1 (e.g. 6.8), used as it is: TT$ / fx = US$, US$ x fx = TT$.
  // No usable rate (null, 0, negative) is a refusal below, never a guess.
  const fx = Number(fxRateAtPurchase);
  const hasRate = Number.isFinite(fx) && fx > 0;

  if (currency === 'USD') {
    if (usdCost > 0) return { cost: usdCost };
    if (ttCost > 0 && hasRate) return { cost: costToUsdExact(ttCost, 'TTD', 1 / fx) as number };
  } else if (currency === 'TTD') {
    if (ttCost > 0) return { cost: ttCost };
    if (usdCost > 0 && hasRate) return { cost: usdToTtdExact(usdCost, 1 / fx) as number };
  }

  return {
    cost: null,
    refusalReason:
      currency === 'USD' || currency === 'TTD'
        ? `cost levels have no ${currency} figure and no FX rate to convert`
        : `invoice currency "${currency}" has no TT$/US$ conversion path`,
  };
}

// An imported or picked cost goes on in the INVOICE's currency: as is, or TT$/US$ converted exact at `ttdCostRate`'s
// rate (D119.9/D119.10). No stated currency, no TT$/US$ path or no rate is refused with its reason, never guessed,
// so a bill's TT$ price never lands on a US$ invoice as US$ (D157.5).
export const NO_SOURCE_CURRENCY_REASON = 'the imported price states no currency';
export type TtdRate = ReturnType<typeof ttdCostRate>;
export interface ImportedLineCostInputs {
  invoiceCurrency: string | null | undefined;
  sourceCurrency: string | null | undefined;
  sourcePrice: number | null | undefined;
  /** The line's TT$-per-US$1 rate from `ttdCostRate`, or its reason for having none. */
  rate: TtdRate;
}

export type ImportedLineCostResult =
  | { cost: number; refusalReason?: undefined }
  | { cost: null; refusalReason: string };

export function resolveImportedLineCost({
  invoiceCurrency,
  sourceCurrency,
  sourcePrice,
  rate,
}: ImportedLineCostInputs): ImportedLineCostResult {
  const price = Number(sourcePrice);
  if (sourcePrice == null || !Number.isFinite(price)) {
    return { cost: null, refusalReason: 'no price on the imported line' };
  }

  const invoiceCode = String(invoiceCurrency || '').trim().toUpperCase();
  if (!invoiceCode) return { cost: null, refusalReason: NO_CURRENCY_REASON };
  const sourceCode = String(sourceCurrency || '').trim().toUpperCase();
  if (!sourceCode) return { cost: null, refusalReason: NO_SOURCE_CURRENCY_REASON };

  if (sourceCode === invoiceCode) {
    return { cost: price };
  }

  const isTtdUsdPair =
    (sourceCode === 'TTD' && invoiceCode === 'USD') || (sourceCode === 'USD' && invoiceCode === 'TTD');
  const why = `priced in ${sourceCode}, this invoice is ${invoiceCode}, and`;
  if (!isTtdUsdPair) return { cost: null, refusalReason: `${why} no FX rate is available to convert it` };
  if (rate.rate === null) return { cost: null, refusalReason: `${why} ${rate.reason}` };

  // The ladder's rate is TT$ per US$1; the converters take US$ per TT$1, inverted once here.
  const ttdToUsd = 1 / rate.rate;
  const converted = sourceCode === 'TTD' ? costToUsdExact(price, 'TTD', ttdToUsd) : usdToTtdExact(price, ttdToUsd);
  return converted == null ? { cost: null, refusalReason: `${why} no FX rate is available to convert it` } : { cost: converted };
}

/** D171.1: bill lines merged onto one invoice line, as one: the summed quantity (6 dp) at the exact quantity-weighted price
 * Σ(qty × price) / Σqty in the bill's currency, for `resolveImportedLineCost` to convert once (price null: a line states
 * none). null when a line states no quantity or the sum is not above 0: nothing merges. */
export function mergedBillLines(parts: Array<{ quantity: number | null; price: number | null }>): { quantity: number; price: number | null } | null {
  if (parts.some((p) => p.quantity == null)) return null;
  const quantity = roundQuantity(parts.reduce((sum, p) => sum + p.quantity!, 0));
  if (quantity == null) return null;
  if (parts.some((p) => p.price == null)) return { quantity, price: null };
  // One price stays exactly that price; the division only weighs prices that differ.
  if (parts.every((p) => p.price === parts[0].price)) return { quantity, price: parts[0].price };
  return { quantity, price: parts.reduce((sum, p) => sum + p.quantity! * p.price!, 0) / quantity };
}

/**
 * D119.10 (D85): the selling price a freshly imported or picked cost goes on at - markup 0, the exact
 * cost rounded UP once by the one owner (`sellingPriceAt`), so the price is a whole-cent price while the
 * line's cost stays exact. No cost is no price (null); a stated 0 stays 0 and a negative (credit) cost
 * keeps its sign - `sellingPriceAt` would turn both into 0, a free line.
 */
export const importedSellingPrice = (cost: number | null): number | null =>
  cost == null ? null : cost > 0 ? sellingPriceAt(cost, 0) : cost;

/**
 * D109.11: the text of a client-invoice line's COST input - the same rule the quote screen
 * shows a US$ cost by (D104.10, `exactCostText`): two decimals, or four when the cost has a
 * sub-cent part. The line's `actualUnitCost` is the exact, unrounded cost on a system-written
 * line (clientInvoice.service.js; the display copies `ttCostPerUom` / `usdCostPerUom` are the
 * rounded ones and are never read here), so it is shown as it is stored, in whatever currency
 * the invoice is in. A cost typed in the Cost cell is saved to the cent, so it reads back with
 * two decimals (a TT$ cost typed on the procurement plan is converted unrounded and may not). Display only: a typed value is still saved rounded to the cent by
 * `parseCellText`, and an unedited field commits nothing.
 */
export function costInputText(storedCost: number | null | undefined): string {
  if (storedCost == null || !Number.isFinite(storedCost)) return '';
  return exactCostText(storedCost);
}

/**
 * D119.9: the ONE TT$-per-US$1 rate a person's TT$ cost is converted at in the line drawer.
 * The first usable (finite, above zero) of, in this order: the rate pinned on the line's own cost
 * (`fxRateAtCost`), the rate the INVOICE is pinned at (`invoiceRate` - the server's one reader,
 * `pinnedUsdToTtd`, via GET /client-invoices/:id/pinned-rate; this file never re-derives the
 * quotation rule), then the Settings rate (`settingsTtdToUsd`, US$ per TT$1, inverted here).
 * The procurement worksheet (`patchProcurementPlanLine`) walks the same ladder on the server.
 * A pinned rate is never replaced by the live one. `invoiceRateKnown` false = the invoice's rate has
 * not loaded (or failed to): without a line pin that is a refusal, not a fall to Settings, which could
 * differ from the server's. Known with a null `invoiceRate` = the invoice has none pinned. None
 * usable: no rate and a reason - the caller disables the input; a rate is never guessed.
 */
export const NO_TTD_RATE_REASON = 'no TT$ rate is pinned on this line or invoice, and none is set in Settings';
export const INVOICE_RATE_UNKNOWN_REASON = 'the invoice\'s pinned TT$ rate has not loaded';
export function ttdCostRate(p: {
  lineRate: number | null | undefined;
  invoiceRate: number | null | undefined;
  invoiceRateKnown: boolean;
  settingsTtdToUsd: number | null | undefined;
}): { rate: number; source: 'line' | 'invoice' | 'settings' } | { rate: null; reason: string } {
  const usable = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0;
  if (usable(p.lineRate)) return { rate: p.lineRate, source: 'line' };
  if (!p.invoiceRateKnown) return { rate: null, reason: INVOICE_RATE_UNKNOWN_REASON };
  if (usable(p.invoiceRate)) return { rate: p.invoiceRate, source: 'invoice' };
  if (usable(p.settingsTtdToUsd)) return { rate: 1 / p.settingsTtdToUsd, source: 'settings' };
  return { rate: null, reason: NO_TTD_RATE_REASON };
}

/**
 * D152.2: which box a person types a line's cost in. 'ttd' = the line is on a US$ invoice AND `ttdCostRate`
 * found a usable rate for it: TT$ is typed, the US$ cost is only worked out from it (read-only). 'single' =
 * anything else (a TT$ invoice, no currency, or a US$ line with no usable rate - including a rate that has
 * not loaded yet): the one cost box in the invoice's own currency, typable. The one place this is decided;
 * the drawer and the grid read it and never look at the currency or the rate themselves.
 */
export type CostEntry = 'ttd' | 'single';
export function costEntryFor(currency: string | null | undefined, found: { rate: number | null }): CostEntry {
  return String(currency ?? '').trim().toUpperCase() === 'USD' && found.rate != null ? 'ttd' : 'single';
}
